// Centralized options for the team search filters.
//
// Seasons are generated dynamically from the current date so we never have to
// hand-maintain a hard-coded list of years in the UI.

export interface FilterOption {
  label: string;
  value: string;
  /** Emoji shown before the label in the dropdown (GameChanger-style). */
  icon?: string;
}

const SEASON_ORDER = ["fall", "summer", "spring", "winter"] as const;

const SEASON_ICONS: Record<(typeof SEASON_ORDER)[number], string> = {
  fall: "🍂",
  summer: "☀️",
  spring: "🌷",
  winter: "❄️",
};

const capitalize = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

/**
 * Build the season filter options, most-recent first.
 *
 * Always starts one year ahead of `now` (teams register for next year's seasons well
 * in advance on GameChanger), so the list rolls forward automatically and never needs
 * a hard-coded year bump.
 *
 * Winter spans a calendar-year boundary (e.g. "Winter 2026-2027" = Dec 2026-Feb 2027),
 * so it's labeled as a range but keyed by its *starting* year to match GameChanger's
 * convention — same `winter_2026` value the API has always received.
 *
 * @param yearsBack How many years (including the lookahead year) to include.
 * @param now       Injectable "current date" — handy for tests.
 *
 * Each option's `value` keeps the existing `name_year` shape (e.g. "spring_2026")
 * so the search request payload is unchanged.
 */
export const generateSeasonOptions = (yearsBack = 6, now: Date = new Date()): FilterOption[] => {
  const startYear = now.getFullYear() + 1;
  const options: FilterOption[] = [];

  for (let year = startYear; year > startYear - yearsBack; year--) {
    for (const season of SEASON_ORDER) {
      if (season === "winter") {
        const winterStartYear = year - 1;
        options.push({
          label: `Winter ${winterStartYear}-${year}`,
          value: `winter_${winterStartYear}`,
          icon: SEASON_ICONS.winter,
        });
      } else {
        options.push({ label: `${capitalize(season)} ${year}`, value: `${season}_${year}`, icon: SEASON_ICONS[season] });
      }
    }
  }

  return options;
};

export const SPORT_OPTIONS: FilterOption[] = [
  { label: "Baseball", value: "baseball", icon: "⚾" },
  { label: "Softball", value: "softball", icon: "🥎" },
];

export const STATE_OPTIONS: FilterOption[] = [
  { label: "Alabama", value: "AL" },
  { label: "Alaska", value: "AK" },
  { label: "Arizona", value: "AZ" },
  { label: "Arkansas", value: "AR" },
  { label: "California", value: "CA" },
  { label: "Colorado", value: "CO" },
  { label: "Connecticut", value: "CT" },
  { label: "Delaware", value: "DE" },
  { label: "Florida", value: "FL" },
  { label: "Georgia", value: "GA" },
  { label: "Hawaii", value: "HI" },
  { label: "Idaho", value: "ID" },
  { label: "Illinois", value: "IL" },
  { label: "Indiana", value: "IN" },
  { label: "Iowa", value: "IA" },
  { label: "Kansas", value: "KS" },
  { label: "Kentucky", value: "KY" },
  { label: "Louisiana", value: "LA" },
  { label: "Maine", value: "ME" },
  { label: "Maryland", value: "MD" },
  { label: "Massachusetts", value: "MA" },
  { label: "Michigan", value: "MI" },
  { label: "Minnesota", value: "MN" },
  { label: "Mississippi", value: "MS" },
  { label: "Missouri", value: "MO" },
  { label: "Montana", value: "MT" },
  { label: "Nebraska", value: "NE" },
  { label: "Nevada", value: "NV" },
  { label: "New Hampshire", value: "NH" },
  { label: "New Jersey", value: "NJ" },
  { label: "New Mexico", value: "NM" },
  { label: "New York", value: "NY" },
  { label: "North Carolina", value: "NC" },
  { label: "North Dakota", value: "ND" },
  { label: "Ohio", value: "OH" },
  { label: "Oklahoma", value: "OK" },
  { label: "Oregon", value: "OR" },
  { label: "Pennsylvania", value: "PA" },
  { label: "Rhode Island", value: "RI" },
  { label: "South Carolina", value: "SC" },
  { label: "South Dakota", value: "SD" },
  { label: "Tennessee", value: "TN" },
  { label: "Texas", value: "TX" },
  { label: "Utah", value: "UT" },
  { label: "Vermont", value: "VT" },
  { label: "Virginia", value: "VA" },
  { label: "Washington", value: "WA" },
  { label: "West Virginia", value: "WV" },
  { label: "Wisconsin", value: "WI" },
  { label: "Wyoming", value: "WY" },
];
